import { Controller } from "@hotwired/stimulus"

// Autozapis formatki posta w tle (notatka ze spotkania). Co `interval` ms porównuje zawartość
// formatki z ostatnio zapisaną i wysyła zmiany z autosave=1 (kontroler nie dopisuje wtedy aktywności).
// Nowy post: pierwszy zapis tworzy rekord, potem formatka przechodzi na PATCH tego rekordu, a adres
// strony na jego edycję - odświeżenie strony nie gubi notatki. Edytor Lexical i intum_select ustawiają
// ukryte pola bez zdarzeń input, dlatego porównujemy migawki zamiast nasłuchiwać zmian.
export default class extends Controller {
  static targets = ["status"]
  static values = {
    interval: { type: Number, default: 5000 },
    postsUrl: String,
    persisted: Boolean,
    savingText: String,
    savedText: String,
    errorText: String,
  }

  connect() {
    this.saving = false
    this.lastSnapshot = null
    // migawka bazowa: edytor normalizuje treść w ukrytym polu dopiero po swoim connect, więc nie bierzemy
    // jej od razu, tylko przy pierwszym klawiszu/kliknięciu (capture - zanim zmieni się wartość) albo po 1 s.
    // Sam timer zgubiłby zmianę wpisaną w pierwszej sekundzie - weszłaby do migawki bazowej
    this.takeBaseline = this.takeBaseline.bind(this)
    this.baselineTimer = setTimeout(this.takeBaseline, 1000)
    this.element.addEventListener("keydown", this.takeBaseline, true)
    this.element.addEventListener("pointerdown", this.takeBaseline, true)
    this.timer = setInterval(() => this.tick(), this.intervalValue)
    this.stop = this.stop.bind(this)
    this.element.addEventListener("submit", this.stop)
  }

  disconnect() {
    this.stop()
    this.element.removeEventListener("submit", this.stop)
    this.element.removeEventListener("keydown", this.takeBaseline, true)
    this.element.removeEventListener("pointerdown", this.takeBaseline, true)
  }

  takeBaseline() {
    clearTimeout(this.baselineTimer)
    if (this.lastSnapshot === null) this.lastSnapshot = this.snapshot()
  }

  // zwykły zapis przyciskiem przejmuje formatkę - autozapis nie może wysłać jej równolegle
  stop() {
    clearTimeout(this.baselineTimer)
    clearInterval(this.timer)
  }

  snapshot() {
    return formSnapshot(new FormData(this.element))
  }

  tick() {
    if (this.saving || this.lastSnapshot === null) return

    const snapshot = this.snapshot()
    if (snapshot === this.lastSnapshot) return

    this.save(snapshot)
  }

  async save(snapshot) {
    this.saving = true
    this.showStatus(this.savingTextValue, false)
    const body = autosaveBody(new FormData(this.element))
    try {
      const response = await fetch(this.element.action, {
        method: "POST", // dla istniejącego posta _method=patch jest w formatce
        body,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const post = await response.json()
      if (!this.persistedValue && post.id) this.switchToUpdate(post.id)
      this.lastSnapshot = snapshot
      this.showStatus(`${this.savedTextValue} ${timeLabel(new Date())}`, false)
    } catch (error) {
      console.warn("[post-autosave]", error.message)
      this.showStatus(this.errorTextValue, true)
    } finally {
      this.saving = false
    }
  }

  // po utworzeniu rekordu kolejne zapisy (także przyciskiem) aktualizują go zamiast tworzyć nowy
  switchToUpdate(id) {
    const url = `${this.postsUrlValue}/${id}`
    this.element.action = url
    const method = document.createElement("input")
    method.type = "hidden"
    method.name = "_method"
    method.value = "patch"
    this.element.prepend(method)
    this.persistedValue = true
    window.history.replaceState(window.history.state, "", `${url}/edit`)
  }

  showStatus(text, isError) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-red-600", isError)
    this.statusTarget.classList.toggle("text-gray-500", !isError)
  }
}

// Migawka formatki do porównania zmian - pola tekstowe w kolejności formatki, bez plików
export function formSnapshot(formData) {
  const pairs = []
  for (const [key, value] of formData) {
    if (typeof value === "string" && key !== "authenticity_token") pairs.push([key, value])
  }
  return JSON.stringify(pairs)
}

// Treść zapisu w tle: pola formatki bez plików (załączniki idą direct uploadem, tu są tylko ich
// signed_id w ukrytym polu) + znacznik autosave
export function autosaveBody(formData) {
  const body = new FormData()
  for (const [key, value] of formData) {
    if (typeof value === "string") body.append(key, value)
  }
  body.append("autosave", "1")
  return body
}

// godzina zapisu w języku strony (np. 19:06 dla pl), a nie przeglądarki
export function timeLabel(date, locale = document.documentElement.lang || undefined) {
  return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
}
